import { FC, useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AllocationMode, MetalKey } from '../constants/sip.constants';
import useRazorpay from 'react-razorpay';
import { userStore } from 'store/UserStore';
import { useHistory, useLocation } from 'react-router-dom';
import Loader from 'shared/common/loader';
import { toast } from 'react-toastify';
import { load } from '@cashfreepayments/cashfree-js';
import SipHttp from '../services/sip.service';
import SipSuccessModal from './SipSuccessModal';
import KycPopup from 'shared/common/kycPopup';
import '../styles/SipSummaryModal.css';

type FormValuesMetals = {
    [key in MetalKey]: { amount: number | '' };
};

const METAL_CODE_MAP: Record<string, string> = {
    gold: 'Au',
    silver: 'Ag',
    platinum: 'Pt',
    palladium: 'Pd',
};

interface SipSummaryModalProps {
    show: boolean;
    values: {
        sipName: string;
        frequency: string;
        sipDuration: number;
        startDate: string;
        paymentMethod: 'payemandate' | 'paymanual' | '';
        emandateType: 'withMandateUPI' | 'withMandate' | '';
        metals: FormValuesMetals;
        investmentAmount: number | '';
        couponApplied: boolean;
        couponName: string;
        autoRenew: boolean;
    };
    allocationMode: AllocationMode;
    goldPercent: number;
    onClose: () => void;
    onProceed: () => void;
}


const SipSummaryModal: FC<SipSummaryModalProps> = ({
    show,
    values,
    allocationMode,
    goldPercent,
    onClose,
    onProceed,
}) => {
    // console.log(values, '@values');

    // console.log(allocationMode,"@allocationMode")

    const history = useHistory();
    const location = useLocation();

    const basePath = location.pathname;

    const [loading, setLoading] = useState<any>(false);
    const [cashfreeLoading, setCashfreeLoading] = useState(false);
    const [cashfreeSubscriptionId, setCashfreeSubscriptionId] = useState('');
    const [sipSuccess, setSipSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    const [isKYCRequired, setIsKYCRequired] = useState(false);

    const Razorpay = useRazorpay();
    const [customerId, user, customer] = userStore((state: any) => [
        state.customerId,
        state.user,
        state.customer,
    ]);

    const [razorpay, setRazorpay] = useState<any>({
        customerId: customerId,
        amount: null,
        paymentMethod: 'NEBK',
    });



    const selectedMetals = Object.values(values.metals).filter(
        (m) => typeof m.amount === 'number' && m.amount > 0
    );

    const totalInvestment =
        allocationMode === 'single'
            ? Number(selectedMetals[0]?.amount || 0)
            : allocationMode === 'goldSilver'
                ? Number(values.investmentAmount || 0)
                : selectedMetals.reduce((sum: any, m) => sum + m.amount, 0);


    const getSingleMetalCode = (): string | null => {
        const validMetals = Object.entries(values.metals).filter(
            ([key, m]) =>
                typeof m.amount === 'number' &&
                m.amount > 0 &&
                METAL_CODE_MAP[key] // ensure it exists in whitelist
        );

        if (validMetals.length !== 1) return null;

        const [metalKey] = validMetals[0];
        return METAL_CODE_MAP[metalKey];
    };

    const buildDailyPayload = () => {
        const validMetals = Object.entries(values.metals)
            .filter(([, m]) => typeof m.amount === 'number' && m.amount > 0);

        if (!validMetals.length) {
            toast.error("Please select at least one metal");
            return null;
        }

        const basePayload = {
            plan_name: values.sipName,
            plan_recurring_amount: Number(totalInvestment),
            plan_currency: "INR",
            duration: String(values.sipDuration),
            subscription_first_charge_time: values.startDate,
            frequency: "daily",
            discount_name: values.couponName || "",
            is_auto_renew: values.autoRenew,
            partnerId: user?.partner?.id,
            sip_type: allocationMode,
        };

        // SINGLE METAL
        if (validMetals.length === 1) {
            const metalKey = validMetals[0][0];
            return {
                ...basePayload,
                metal: METAL_CODE_MAP[metalKey],
                is_multi_metal: false,
            };
        }

        // MULTI METAL (goldSilver or multi)
        const totalAmount = validMetals.reduce(
            (sum, [, m]) => sum + Number(m.amount),
            0
        );

        const multiDetails = validMetals.map(([key, m]) => ({
            metal: METAL_CODE_MAP[key],
            amt: Number(m.amount),
            pct: Math.round(
                (Number(m.amount) / totalAmount) * 100
            ),
        }));

        return {
            ...basePayload,
            is_multi_metal: true,
            multi_metal_details: multiDetails,
        };
    };


    const buildMultiMetalDetails = () => {
        const metals = values.metals;

        const validMetals = Object.values(metals).filter(
            (m) => typeof m.amount === 'number' && m.amount > 0
        );


        const totalAmount = validMetals.reduce((sum: any, m) => sum + m.amount, 0);

        return Object.entries(metals)
            .filter(([, m]) => typeof m.amount === 'number' && m.amount > 0)
            .map(([key, m]) => ({
                metal: METAL_CODE_MAP[key],
                amt: Number(m.amount),
                pct: Math.round(
                    (Number(m.amount) / totalAmount) * 100
                ),
            }));
    };

    const checkPanRequirement = () => {
        console.log(customer, "@customer");
        const isVerified = customer?.kyc?.pan?.isVerified;

        console.log(isVerified, "!@isVerified");

        if (!isVerified) {
            setIsKYCRequired(true);
            return false;
        }

        return true;
    };
    const onKycModalClose = () => {
        setIsKYCRequired(false);
    };

    const multiMetalDetails = buildMultiMetalDetails();

    const singleMetalCode = getSingleMetalCode();

    const baseSipPayload = {
        plan_name: values.sipName,
        metal: singleMetalCode,
        frequency: values.frequency,
        installment_amt: totalInvestment,
        partnerId: user?.partner?.id,
        // is_multi_metal: multiMetalDetails.length > 1,
        // multi_metal_details: multiMetalDetails,
        duration: values.sipDuration,
        start_date: values.startDate,
        bank: "",
        grams: "",
        isupi: false,
        customer_id: customerId,
        is_payment_manual: true,
        discount_name: values.couponName,
        is_auto_renew: values.autoRenew
    };


    const onSuccessRpyMandate = async () => {
        redirectToPassbook("Autopay request has been sent successfully");
    };

    const onFailureRpyMandate = async () => {
        history.push(`${basePath}?tab=create`);

        setTimeout(() => {
            toast.error('Payment Failed');
        }, 1000);
    };
    /**
     * Download invoice pdf
     */
    const setInitialStateRazorpay = () => {
        setRazorpay({
            customer_id: '',
            order_id: '',
        });
    };


    const placeOrder = async (successMessage?: any) => {
        // console.log(successMessage, '@successMessage111');
        const isPanVerified = checkPanRequirement();
        if (!isPanVerified) return;


        try {
            if (values.paymentMethod == 'paymanual') {
                setLoading(true);
                const resp = await SipHttp.createSIPWithoutEM(customerId, baseSipPayload);
                if (resp?.status === 200) {
                    setLoading(false);
                    toast.success("SIP created successfully");
                    onClose();
                    redirectToPassbook("SIP created successfully");
                }
            } else {
                let resp;
                if (values.emandateType == 'withMandateUPI') {
                    if (values?.frequency === 'daily') {
                        const dailyPayload = buildDailyPayload();
                        if (!dailyPayload) return;

                        resp = await SipHttp.createDailySIPWithEM(dailyPayload);
                        setCashfreeSubscriptionId(resp?.data?.data?.subscriptionId);
                    }
                    else {
                        resp = await SipHttp.createSIPWithEM(
                            customerId,
                            user?.userId,
                            true,
                            values?.frequency,
                            successMessage?.name
                        );
                    }
                } else {
                    resp = await SipHttp.createSIPWithEM(
                        customerId,
                        user?.userId,
                        false,
                        values?.frequency,
                        successMessage?.name
                    );
                }
                if (resp?.status == 200) {
                    await authFunc(resp?.data?.data);
                }
            }
        } catch (err) {
            toast.error('Something Went Wrong');
            setLoading(false);

            console.log(err);
        }
    };

    const cashfree = async (subId) => {
        setCashfreeLoading(true);
        const res = await load({
            mode: process.env.REACT_APP_CASHFREE_MODE,
        });
        res
            .subscriptionsCheckout({
                subsSessionId: subId,
                redirectTarget: '_blank',
            })
            .then(function (result) {
                // setCashfreeLoading(false);
                if (result.error) {
                    document.getElementById('paymentMessage').innerHTML =
                        result.error.message;
                }
            });
        // console.log('cashfree', res);
    };

    const authFunc = async (rpyData) => {

        const { rpyOrderId, rpyCustomerId } = rpyData;

        const options: any = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            name: 'CoinBazaar Digital',
            description: 'Create SIP',
            image:
                'https://ebullion-dev.s3.ap-south-1.amazonaws.com/assets/eb-logo.png',

            order_id: rpyOrderId,
            customer_id: rpyCustomerId,
            recurring: 1,

            handler: async (response: any) => {
                setLoading(true);
                if (response.razorpay_payment_id) {
                    await onSuccessRpyMandate();
                } else {
                    await onFailureRpyMandate();
                }
                setInitialStateRazorpay();
            },
            prefill: {
                name: user.displayName,
                email: user.email.value,
                contact: user.mobile.value,
            },

            notes: {
                plan_name: String(values?.sipName || ""),
                frequency: String(values?.frequency || ""),
                installment_amt: String(totalInvestment || 0),

                metal:
                    multiMetalDetails.length === 1
                        ? String(multiMetalDetails[0]?.metal)
                        : "",

                duration: String(values?.sipDuration || ""),
                start_date: String(values?.startDate || ""),
                customer_id: String(customerId || ""),

                is_payment_manual: String(values?.paymentMethod === "paymanual"),
                isupi: String(values?.emandateType === "withMandateUPI"),

                discount_name: String(values?.couponName || ""),
                is_auto_renew: String(values?.autoRenew),

                partnerId: String(customer?.partner?.id || ""),

                is_multi_metal: String(multiMetalDetails?.length > 1),

                sip_type: allocationMode,

                multi_metal_details:
                    multiMetalDetails?.length > 1
                        ? JSON.stringify(multiMetalDetails)
                        : "",
            },

            theme: {
                hide_topbar: false,
                color: '#141034',
                backdrop_color: 'rgba(0, 0, 0, 0.90)',
            },

        };

        const razorPay = new Razorpay(options);
        // console.log('open cashfree', values?.frequency);

        if (values?.frequency !== 'daily') {
            razorPay.open();
        } else {
            const subId = rpyData?.subscriptionSessionId;
            cashfree(subId);
        }
    };

    const checkDailySipStatus = async () => {
        try {
            const data = { subscriptionId: cashfreeSubscriptionId };
            const res = await SipHttp.createDailySIPStatus(data);
            // console.log('cashstatus', res);
            if (res?.data?.data?.status === "ACTIVE") {
                setCashfreeLoading(false);

                onClose();

                redirectToPassbook("SIP created successfully");
            }

        } catch (err) {
            console.log(err);
        }
    };



    const redirectToPassbook = (message: string) => {
        setSuccessMessage(message);
        setSipSuccess(true);
    };


    const handleSuccessComplete = () => {
        setSipSuccess(false);

        onClose();

        history.push(
            `${basePath}?tab=passbook&toastMessage=` +
            encodeURIComponent(successMessage)
        );
    };

    useEffect(() => {
        if (isKYCRequired && customer?.kyc?.pan?.isVerified) {
            setIsKYCRequired(false);
            placeOrder();
        }
    }, [customer?.kyc?.pan?.isVerified]);


    useEffect(() => {
        if (!cashfreeLoading) return; // Ensure it only runs when `cashfreeLoading` is true

        const intervalId = setInterval(() => {
            checkDailySipStatus();
        }, 5000);

        const timeoutId = setTimeout(() => {
            setCashfreeLoading(false); // Stop further API calls
            clearInterval(intervalId);
            // history.push('/sip?tab=sippassbook');

        }, 120000);

        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, [cashfreeLoading, cashfreeSubscriptionId]);

    return (
        <>
            {loading || cashfreeLoading ? <Loader /> : null}
            {show && (
                <Modal show={show} onHide={onClose} centered backdrop='static'>
                    <Modal.Body className='sip-summary'>
                        <header className='sip-summary__header'>
                            <h5 className='sip-summary__title'>Confirm Your SIP</h5>
                            <p className='sip-summary__subtitle'>
                                Review your SIP details before proceeding to payment
                            </p>
                        </header>

                        <section className='sip-summary__card'>
                            <div className='sip-section'>
                                <SummaryRow label='SIP Name' value={values.sipName || '-'} />
                                <SummaryRow label='Frequency' value={values.frequency.toUpperCase()} />
                                <SummaryRow
                                    label='Duration'
                                    value={`${values.sipDuration} Months`}
                                />
                                <SummaryRow
                                    label='Start Date'
                                    value={values.startDate || '-'}
                                />
                                <SummaryRow
                                    label="Payment Method"
                                    value={
                                        values.paymentMethod === 'payemandate'
                                            ? `Autopay (${values.emandateType === 'withMandateUPI'
                                                ? 'UPI'
                                                : values.emandateType === 'withMandate'
                                                    ? 'Net Banking'
                                                    : ''
                                            })`
                                            : 'Manual Payment'
                                    }
                                />


                                {values.couponApplied && (
                                    <SummaryRow
                                        label='Coupon'
                                        value={values.couponApplied ? values.couponName : 'None'}
                                    />
                                )}

                                {values.autoRenew && (
                                    <SummaryRow
                                        label='Auto-Renew'
                                        value={values.autoRenew ? 'Enabled' : 'Disabled'}
                                    />
                                )}
                            </div>

                            <div className='sip-divider' />

                            <div className='sip-section'>
                                <div className='sip-section__title'>Investment Allocation</div>

                                {allocationMode === 'single' &&
                                    Object.entries(values.metals)
                                        .filter(([, m]) => m.amount)
                                        .map(([key, m]) => (
                                            <div key={key} className='allocation-row'>
                                                <span className='allocation-label'>{key}</span>
                                                <strong className='allocation-value'>
                                                    ₹{m.amount || 0}
                                                </strong>
                                            </div>
                                        ))}

                                {allocationMode === 'goldSilver' && (
                                    <>
                                        <div className='allocation-row'>
                                            <span className='allocation-label'>Gold</span>
                                            <strong className='allocation-value'>
                                                ₹{values.metals.gold.amount ?? 0}
                                            </strong>
                                        </div>
                                        <div className='allocation-row'>
                                            <span className='allocation-label'>Silver</span>
                                            <strong className='allocation-value'>
                                                ₹{values.metals.silver.amount ?? 0}
                                            </strong>
                                        </div>
                                    </>
                                )}

                                {allocationMode === 'multi' &&
                                    Object.entries(values.metals)
                                        .filter(([, m]) => typeof m.amount === 'number' && m.amount > 0)
                                        .map(([key, m]) => (
                                            <div key={key} className='allocation-row'>
                                                <span className='allocation-label'>
                                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                                </span>
                                                <strong className='allocation-value'>
                                                    ₹{m.amount}
                                                </strong>
                                            </div>
                                        ))}
                            </div>

                            <div className='sip-divider soft' />

                            <div className='final-amount'>
                                <span className='final-label'>Total Investment</span>
                                <strong className='final-value'>₹{totalInvestment}</strong>
                            </div>
                        </section>

                        <footer className='sip-summary__actions'>
                            <Button variant='secondary' onClick={onClose}>
                                Edit SIP
                            </Button>
                            <Button className='primary' onClick={placeOrder}>
                                Proceed to Payment
                            </Button>
                        </footer>
                    </Modal.Body>
                </Modal>
            )}

            <SipSuccessModal
                show={sipSuccess}
                duration={2200}
                onComplete={handleSuccessComplete}
            />

            <KycPopup
                isKYCPopupDisplayed={isKYCRequired}
                isPanVerficationRequired={true}
                closeModal={onKycModalClose}
                hideModal={onKycModalClose}
            />
        </>
    );
};

const SummaryRow = ({
    label,
    value,
}: {
    label: string;
    value: string | number;
}) => (
    <div className='summary-row'>
        <span className='summary-label'>{label}</span>
        <strong className='summary-value'>{value}</strong>
    </div>
);

export default SipSummaryModal;
